import { useLazyRef } from '../../shared/lib/hooks.ts'
import { useAppModule } from '../../features/appContext.ts'
import { cn, useUnmount } from '../../shared/lib/utils.ts'
import { classed } from '@tw-classed/react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useUserTestLessonStore,
  UserTestLessonStoreContext,
  UserTestView,
} from '../../features/userTestLessons/userTestLessonView.tsx'
import { motion } from 'framer-motion'
import { MainContainer } from '../../shared/ui/card/card.tsx'
import { ChatPageShell } from './chatPageShell.tsx'
import { useSnapshot } from 'valtio'
import { useEffect, useRef } from 'react'
import { urls } from '../../shared/urls.ts'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import { isTestAccessible } from '../../shared/api/userTestsApi.ts'
import { UserTest } from '../../shared/api/userTestsApi.ts'
import { useIntl } from 'react-intl'

export function UserTestChat(props: { tag: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as {
    testType: string
    testName: string
    test?: UserTest
  } | null
  const startTestCalled = useRef(false)

  const appModule = useAppModule()
  const intl = useIntl()

  const userTestLessonStoreRef = useLazyRef(() => {
    const userTestLessonStore = appModule.userTestLessonStore()
    return {
      userTestLessonStore,
    }
  })

  const storeState = useSnapshot(
    userTestLessonStoreRef.current.userTestLessonStore.state,
  )

  useEffect(() => {
    const store = userTestLessonStoreRef.current.userTestLessonStore
    if (startTestCalled.current) return

    const test = state?.test
    if (test && !isTestAccessible(test)) {
      appModule.toastStore.showError(
        intl.formatMessage({ id: 'test.notAvailable' }),
      )
      navigate(urls.testMode, { replace: true })
      return
    }

    startTestCalled.current = true
    void store.startTest(props.tag, state?.testType || 'Input')
  }, [
    props.tag,
    state?.testType,
    state?.test,
    intl,
    appModule.toastStore,
    navigate,
  ])

  useEffect(() => {
    if (storeState.testAccessError) {
      userTestLessonStoreRef.current.userTestLessonStore.state.testAccessError =
        null
      navigate(urls.testMode, { replace: true })
    }
  }, [storeState.testAccessError, navigate])

  useEffect(() => {
    if (storeState.programTag && storeState.programTag !== props.tag) {
      navigate(urls.userTest(storeState.programTag), {
        replace: true,
        state: location.state,
      })
    }
  }, [storeState.programTag, props.tag, navigate, location.state])

  useUnmount(() => {
    appModule.speechService.dispose()
  })

  const progressBlock = <StageProgressBlockMobile />

  return (
    <UserTestLessonStoreContext.Provider value={userTestLessonStoreRef.current}>
      <ChatPageShell>
        <div className={cn('relative flex size-full')}>
          <MainContainer className="flex flex-col" enable={true}>
            {progressBlock}
            <UserTestChatContainer data-testid="user-test-chat-page">
              <UserTestView />
            </UserTestChatContainer>
          </MainContainer>
        </div>
      </ChatPageShell>
    </UserTestLessonStoreContext.Provider>
  )
}

function UserTestProgressBar() {
  const store = useUserTestLessonStore().userTestLessonStore
  const state = useSnapshot(store.state)
  const exerciseCount = state.exerciseCount
  const currentIndex = state.currentIndex
  const isLessonCompleted = state.lessonCompleted
  const exercisesInfo = state.exercises

  if (exerciseCount === null) return null
  return (
    <div className="flex w-full gap-4">
      {Array.from({ length: exerciseCount }).map((_, outerIndex) => {
        const isActive = isLessonCompleted
          ? outerIndex < currentIndex
          : outerIndex + 1 < currentIndex

        return (
          <ProgressBarElement
            key={`progress-bar-${outerIndex}`}
            isActive={isActive}
            animationDuration={0.5}
          />
        )
      })}
    </div>
  )
}

const ProgressBarElement = ({
  isActive,
  animationDuration,
}: {
  isActive: boolean
  animationDuration: number
}) => {
  return (
    <motion.div className="h-6 w-full overflow-hidden rounded-full bg-blue8 ">
      <motion.div
        className="h-full bg-blue7"
        initial={{ width: 0 }}
        animate={{ width: isActive ? '100%' : '0%' }}
        transition={{ duration: animationDuration, ease: 'easeInOut' }}
      />
    </motion.div>
  )
}

function StageProgressBlockMobile() {
  const store = useUserTestLessonStore().userTestLessonStore
  const state = useSnapshot(store.state)
  const isLessonCompleted = state.lessonCompleted
  const navigate = useNavigate()

  const handleBack = () => {
    navigate(urls.testMode)
  }

  return (
    <div className="fixed left-0 top-0 z-20 flex h-100 w-full items-center bg-demo-mobile-header px-16 backdrop-blur-4 xl:static">
      <div className="flex items-center">
        {!isLessonCompleted && (
          <button onClick={handleBack} className="xl:hidden w-24">
            <Icon iconName="left" />
          </button>
        )}
      </div>
      <div
        className={cn(
          'flex flex-1 gap-6 text-start text-18 font-bold',
          isLessonCompleted ? 'ml-24' : 'ml-16',
        )}
      >
        <UserTestProgressBar />
      </div>
    </div>
  )
}

export const UserTestChatContainer = classed.div(
  'flex w-full flex-1 justify-between gap-24 overflow-hidden bg-white sm:rounded-tl-8 sm:bg-white sm:px-36 sm:pt-36',
)
